import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from 'src/config';

interface DeepLinkParams {
    vehicle: string;
    view: string | null;
    startDateTime: string | null;
    endDateTime: string | null;
    source: 'url' | 'pending';
}

interface PendingDeepLink {
    vehicle: string;
    view: string | null;
    startDateTime: string | null;
    endDateTime: string | null;
    capturedAt: number;
}

interface HistoryMessageParams {
    vehicle: string;
    startDateTime: string;
    endDateTime: string;
}

/**
 * Deep-link helper for OMS.RA -> OMS.Map integration.
 * URL is primary source. A short-lived one-time pending fallback is kept only
 * to recover deep links when MSAL redirect strips query params.
 */
@Injectable({
    providedIn: 'root'
})
export class DeepLinkService {
    public readonly vehicleSelectSource = 'DeepLink';
    public readonly vehicleLoadTimeoutMs = 5000;

    private static readonly _MSG_OPEN_VEHICLE = 'oms-map.openVehicle';
    private static readonly _MSG_OPEN_HISTORY = 'oms-map.openHistory';
    private static readonly _MSG_READY = 'oms-map.ready';
    private static readonly _VEHICLE_ID_PATTERN = /^[a-zA-Z0-9]{1,20}$/;
    private static readonly _DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})?$/;
    private static readonly _PENDING_KEY = 'oms.map.deepLink.pending';
    private static readonly _PENDING_MAX_AGE_MS = 90 * 1000;

    private readonly _configService = inject(ConfigService);

    /**
     * Capture current URL deep-link as one-time pending state.
     * Called very early (before MSAL redirect handling).
     */
    public capturePendingFromLocation(): void {
        const params = this._extractParamsFromLocation();
        if (!params.vehicle || !this._isValidVehicleId(params.vehicle)) {
            this._clearPending();
            return;
        }

        const pending: PendingDeepLink = {
            vehicle: params.vehicle,
            view: params.view ?? null,
            startDateTime: params.startDateTime ?? null,
            endDateTime: params.endDateTime ?? null,
            capturedAt: Date.now()
        };

        try {
            sessionStorage.setItem(DeepLinkService._PENDING_KEY, JSON.stringify(pending));
        } catch {
            // Storage may be unavailable.
        }
    }

    /**
     * URL-first deep-link resolution, with one-time pending fallback for MSAL redirect recovery.
     */
    public getInitialDeepLinkParams(route: ActivatedRoute): DeepLinkParams | null {
        const query = route.snapshot.queryParamMap;
        const vehicle = query.get('vehicle');

        if (vehicle) {
            if (!this._isValidVehicleId(vehicle)) {
                return null;
            }
            return {
                vehicle,
                view: query.get('view'),
                startDateTime: query.get('startDateTime'),
                endDateTime: query.get('endDateTime'),
                source: 'url'
            };
        }

        const pending = this._consumePending();
        if (!pending || !this._isValidVehicleId(pending.vehicle)) {
            return null;
        }

        return {
            vehicle: pending.vehicle,
            view: pending.view,
            startDateTime: pending.startDateTime,
            endDateTime: pending.endDateTime,
            source: 'pending'
        };
    }

    // Backward-compatible helper used in tests and older call sites.
    public getInitialVehicleFromRoute(route: ActivatedRoute): string | null {
        const vehicle = route.snapshot.queryParamMap.get('vehicle');
        if (!vehicle || !this._isValidVehicleId(vehicle)) {
            return null;
        }
        return vehicle;
    }

    public registerVehicleMessageListener(onVehicle: (vehicle: string) => void): () => void {
        const handler = (event: MessageEvent) => {
            const data = event?.data;
            if (!data || typeof data !== 'object' || data.type !== DeepLinkService._MSG_OPEN_VEHICLE) {
                return;
            }

            if (!this._isAllowedOrigin(event.origin)) {
                console.warn('DeepLink: Rejected message from untrusted origin:', event.origin);
                return;
            }

            const vehicle = this._extractVehicleFromMessage(data);
            if (!vehicle) {
                console.warn('DeepLink: Invalid or missing vehicle in message');
                return;
            }

            // Vehicle update from postMessage should invalidate pending launch fallback.
            this._clearPending();
            onVehicle(vehicle);
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }

    public registerHistoryMessageListener(onHistory: (params: HistoryMessageParams) => void): () => void {
        const handler = (event: MessageEvent) => {
            const data = event?.data;
            if (!data || typeof data !== 'object' || data.type !== DeepLinkService._MSG_OPEN_HISTORY) {
                return;
            }

            if (!this._isAllowedOrigin(event.origin)) {
                console.warn('DeepLink: Rejected message from untrusted origin:', event.origin);
                return;
            }

            const historyParams = this._extractHistoryFromMessage(data);
            if (!historyParams) {
                console.warn('DeepLink: Invalid or missing history payload in message');
                return;
            }

            this._clearPending();
            onHistory(historyParams);
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }

    public notifyOpenerReady(): void {
        try {
            if (!window.opener) {
                return;
            }
            window.opener.postMessage({ type: DeepLinkService._MSG_READY }, '*');
        } catch {
            // Ignore - opener may be closed or cross-origin restricted
        }
    }

    private _extractParamsFromLocation(): { vehicle: string | null; view: string | null; startDateTime: string | null; endDateTime: string | null } {
        const params = this._getLocationSearchParams();
        return {
            vehicle: params.get('vehicle'),
            view: params.get('view'),
            startDateTime: params.get('startDateTime'),
            endDateTime: params.get('endDateTime')
        };
    }

    private _getLocationSearchParams(): URLSearchParams {
        const search = window.location.search;
        const hash = window.location.hash ?? '';
        const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : '';
        return new URLSearchParams(search || hashQuery);
    }

    private _consumePending(): PendingDeepLink | null {
        try {
            const raw = sessionStorage.getItem(DeepLinkService._PENDING_KEY);
            sessionStorage.removeItem(DeepLinkService._PENDING_KEY);
            if (!raw) return null;

            const parsed = JSON.parse(raw) as Partial<PendingDeepLink>;
            if (!parsed || typeof parsed !== 'object') return null;
            if (typeof parsed.capturedAt !== 'number') return null;
            if (Date.now() - parsed.capturedAt > DeepLinkService._PENDING_MAX_AGE_MS) return null;
            if (typeof parsed.vehicle !== 'string') return null;

            return {
                vehicle: parsed.vehicle,
                view: parsed.view ?? null,
                startDateTime: parsed.startDateTime ?? null,
                endDateTime: parsed.endDateTime ?? null,
                capturedAt: parsed.capturedAt
            };
        } catch {
            return null;
        }
    }

    private _clearPending(): void {
        try {
            sessionStorage.removeItem(DeepLinkService._PENDING_KEY);
        } catch {
            // ignore
        }
    }

    private _isValidVehicleId(vehicle: string): boolean {
        return DeepLinkService._VEHICLE_ID_PATTERN.test(vehicle);
    }

    private _isAllowedOrigin(origin: string): boolean {
        const allowedOrigins = this._configService.deepLinkAllowedOrigins;
        return allowedOrigins.includes(origin) || origin === window.location.origin;
    }

    private _extractVehicleFromMessage(data: any): string | null {
        const vehicle = this._normalizeString(data.vehicle);

        if (!vehicle || !this._isValidVehicleId(vehicle)) {
            return null;
        }

        return vehicle;
    }

    private _extractHistoryFromMessage(data: any): HistoryMessageParams | null {
        const vehicle = this._extractVehicleFromMessage(data);
        const startDateTime = this._normalizeString(data.startDateTime);
        const endDateTime = this._normalizeString(data.endDateTime);

        if (!vehicle || !this._isValidDateTime(startDateTime) || !this._isValidDateTime(endDateTime)) {
            return null;
        }

        const start = Date.parse(startDateTime);
        const end = Date.parse(endDateTime);
        if (Number.isNaN(start) || Number.isNaN(end) || start > end) {
            return null;
        }

        return { vehicle, startDateTime, endDateTime };
    }

    private _isValidDateTime(value: string): boolean {
        if (!value || !DeepLinkService._DATE_TIME_PATTERN.test(value)) {
            return false;
        }

        return !Number.isNaN(Date.parse(value));
    }

    private _normalizeString(value: unknown): string {
        return typeof value === 'string'
            ? value.trim()
            : String(value ?? '').trim();
    }
}